import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Card, CardContent } from '@/components/ui/card'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/')({
  component: CrmDashboard,
})

function CrmDashboard() {
  const t = useTranslations()
  // Leads in the caller's queue. The list procedure scopes to grants
  // (own/team/all) so a non-admin caller sees only their own records.
  const leads = useQuery(orpc.crm.leads.list.queryOptions({ input: { limit: 50, offset: 0 } }))
  // Opportunity list carries `weightedAmount` from the backend
  // (amount * probability / 100), already FX-normalised at creation.
  const opps = useQuery(
    orpc.crm.opportunities.list.queryOptions({ input: { limit: 200, offset: 0 } }),
  )
  // Activities list supports `onlyMine` + `onlyOverdue` filters.
  const overdue = useQuery(
    orpc.crm.activities.list.queryOptions({
      input: { limit: 200, offset: 0, onlyMine: true, onlyOverdue: true },
    }),
  )

  const leadRows = (leads.data as Array<Record<string, unknown>>) ?? []
  const openLeadCount = leadRows.filter((l) => {
    const status = l.status as string | undefined
    return status !== 'converted' && status !== 'unqualified'
  }).length

  const oppRows = (opps.data as Array<{ weightedAmount?: string | null }>) ?? []
  const oppWeightedSum = oppRows.reduce((s, o) => s + Number(o.weightedAmount ?? '0'), 0)

  const overdueRows = (overdue.data as Array<unknown>) ?? []
  const overdueCount = overdueRows.length

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">{t('crm.dashboard.title')}</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-muted-foreground text-sm">{t('crm.dashboard.leadsInQueue')}</div>
            <div className="text-3xl font-bold">{openLeadCount}</div>
            <Link to="/crm/leads" className="text-sm text-primary">
              {t('crm.dashboard.viewAll')}
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-muted-foreground text-sm">
              {t('crm.dashboard.weightedPipeline')}
            </div>
            <div className="text-3xl font-bold">${oppWeightedSum.toFixed(0)}</div>
            <Link to="/crm/opportunities" className="text-sm text-primary">
              {t('crm.dashboard.viewPipeline')}
            </Link>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-muted-foreground text-sm">
              {t('crm.dashboard.overdueActivities')}
            </div>
            <div className="text-3xl font-bold">{overdueCount}</div>
            <Link to="/crm/activities" className="text-sm text-primary">
              {t('crm.dashboard.viewAll')}
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
